import {
    DropdownItem,
    DropdownV2,
    Icon,
    Input,
    Text,
    PriorityIcon,
    Scrollbar
} from '@priority-software/priority-style-react';
import React, {useCallback, useState} from 'react';
import cx from 'classnames';
import {isRTL} from '../../../../../../../Localization';
import {texts} from '../../texts';

import styles from './styles.module.scss';
import {useScroll} from '../../../../../../../hooks/dropdown/useScroll';
import {useKeyboardNavigation} from '../../../../../../../components/Common/FieldSearch/hooks/useKeyboardNavigation';
import {Column} from '../../../../../../../stores/Form';
import {keys} from '../../../../../../../constants/KeyCodes';
import {createDataTestId} from '../../../../../../../utils/dataTestId';

type Props = {
    columnsData: Column[];
    onSelect: (column: Column) => void;
    autoFocus?: boolean;
};

export const ColumnsDropdown = ({columnsData, onSelect, autoFocus}: Props) => {
    const [isOpen, setOpen] = useState(autoFocus);
    const [columns, setColumns] = useState(columnsData);
    const [searchValue, setSearchValue] = useState('');

    const {focusedIndex, handleKeyboardNavigation} = useKeyboardNavigation({
        initialFocusedIndex: 0,
        listLength: columns.length,
        pageSize: 1,
        onSelect: () => {
            handleSelect(columns[focusedIndex]);
        }
    });

    const handleNavigation = e => {
        if (e.key === keys.ESC) {
            setOpen(false);
            setSearchValue('');
            return;
        }
        handleKeyboardNavigation(e);
    };

    const {itemsRef} = useScroll({items: columns, focusedItemIndex: focusedIndex});

    const handleSelect = (column: Column) => {
        setSearchValue(column.Title);
        onSelect(column);
        setOpen(false);
    };

    const handleSearchChange = useCallback(
        e => {
            const value = e.target.value;
            setSearchValue(value);
            setOpen(true);
            setColumns(
                columnsData.filter(column =>
                    column.Title.toLowerCase().includes(value.toLowerCase())
                )
            );
        },
        [columnsData]
    );
    const handleOpenInputDropdown = useCallback(e => {
        setOpen(prev => {
            return !prev;
        });
    }, []);

    const handleClose = useCallback((e: Event) => {
        e.stopPropagation();
    }, []);

    return (
        <DropdownV2
            {...createDataTestId('dropdown-container', true)}
            focusOnOpen
            isOpen={isOpen}
            componentClasses={styles.dropdown}
            contentComponentClasses={cx(styles.dropdownMenu, 'columns-dropdown')}
            onClose={handleClose}
            trigger={
                <Input
                    autoFocus={autoFocus}
                    placeholder={texts.fieldSearchPlaceholder}
                    direction={isRTL() ? 'rtl' : 'ltr'}
                    value={searchValue}
                    componentClasses={styles.searchInput}
                    onChange={handleSearchChange}
                    onMouseDown={handleOpenInputDropdown}
                    {...createDataTestId('trigger-input', true)}
                >
                    <Icon
                        icon={isOpen ? PriorityIcon.arrowUp : PriorityIcon.arrowDown}
                        skin="standart"
                        size={'small'}
                        style={{margin: '0 12px'}}
                    />
                </Input>
            }
            eventTypes={['touchstart', 'mousedown']}
            onKeyDown={handleNavigation}
        >
            {columns?.length > 0 ? (
                <Scrollbar autoHeight autoHide={false} rtl={isRTL()}>
                    {columns.map((column, idx) => (
                        <div
                            key={column.Name}
                            className={styles.dropdownItem}
                            ref={itemsRef.current[idx]}
                        >
                            <DropdownItem
                                itemBorder={true}
                                data={{value: column.ID as number, label: column.Title}}
                                isSelected={false}
                                isFocused={idx === focusedIndex}
                                onSelect={() => handleSelect(column)}
                            />
                        </div>
                    ))}
                </Scrollbar>
            ) : (
                <Text
                    skin="paragraph-2"
                    textColor="secondary-400"
                    componentClasses={styles.emptyOptions}
                >
                    {texts.fieldSearchEmptyText}
                </Text>
            )}
        </DropdownV2>
    );
};
